import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { reviews } from "@/mocks/reviews";
import Highlight from "@/components/base/Highlight";

interface SearchResult {
  key: string;
  typeLabel: string;
  icon: string;
  title: string;
  meta: string;
  to: string;
}

interface GlobalSearchProps {
  variant?: "header" | "hero";
  placeholder?: string;
}

export default function GlobalSearch({
  variant = "header",
  placeholder = "搜索电影、资讯或影评…",
}: GlobalSearchProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);

  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const list: SearchResult[] = [];

    movies.forEach((m) => {
      if (
        m.title.toLowerCase().includes(q) ||
        m.originalTitle.toLowerCase().includes(q) ||
        m.synopsis.toLowerCase().includes(q) ||
        m.tags.some((t) => t.toLowerCase().includes(q))
      ) {
        list.push({
          key: `movie-${m.id}`,
          typeLabel: "电影",
          icon: "ri-film-line",
          title: m.title,
          meta: `${m.year} · ${m.type} · 评分 ${m.rating}`,
          to: `/detail/${m.id}`,
        });
      }
    });

    newsList.forEach((n) => {
      if (n.title.toLowerCase().includes(q) || n.summary.toLowerCase().includes(q)) {
        list.push({
          key: `news-${n.id}`,
          typeLabel: "资讯",
          icon: "ri-newspaper-line",
          title: n.title,
          meta: `${n.date} · ${n.category}`,
          to: `/news/${n.id}`,
        });
      }
    });

    reviews.forEach((r) => {
      if (
        r.title.toLowerCase().includes(q) ||
        r.summary.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q))
      ) {
        list.push({
          key: `review-${r.id}`,
          typeLabel: "影评",
          icon: "ri-chat-quote-line",
          title: r.title,
          meta: `${r.author} · 评分 ${r.rating}`,
          to: `/reviews/${r.id}`,
        });
      }
    });

    return list.slice(0, 9);
  }, [query]);

  const go = (to: string) => {
    setOpen(false);
    setQuery("");
    navigate(to);
  };

  const isHero = variant === "hero";

  return (
    <div ref={boxRef} className="relative w-full">
      <div
        className={
          isHero
            ? "flex items-center gap-2 rounded-full bg-background-50 px-5 py-3 shadow-lg"
            : "flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2"
        }
      >
        <i className="ri-search-line text-foreground-500 text-base"></i>
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && results[0]) go(results[0].to);
          }}
          placeholder={placeholder}
          className="w-full min-w-0 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="清空搜索"
            className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
          >
            <i className="ri-close-circle-line text-base"></i>
          </button>
        )}
      </div>

      {open && query.trim() && (
        <div
          className={`absolute left-0 right-0 mt-2 overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-xl ${
            isHero ? "" : "z-50"
          }`}
        >
          {results.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-foreground-500">
              <i className="ri-emotion-sad-line text-xl"></i>
              <p className="mt-2">没有找到相关内容，换个关键词试试</p>
              <p className="mt-1 text-xs text-foreground-400">（演示搜索，基于站内示例数据）</p>
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto py-1">
              {results.map((r) => (
                <li key={r.key}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => go(r.to)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-primary-50"
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                      <i className={`${r.icon} text-base`}></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-800">
                          {r.typeLabel}
                        </span>
                      </span>
                      <span className="mt-1 block truncate text-sm font-medium text-foreground-950">
                        <Highlight text={r.title} query={query} />
                      </span>
                      <span className="mt-0.5 block text-xs text-foreground-500">{r.meta}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-background-200 bg-background-100 px-4 py-2 text-[11px] text-foreground-500">
            演示搜索 · 结果来自站内示例数据
          </div>
        </div>
      )}
    </div>
  );
}